// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { getTranslations, type TranslationKeys, Locale, LOCALE_OPTIONS } from '@vn-grc/shared';
import { apiClient } from '../services/api-client.js';
import { logWarning } from '../utils/optional-operation-log.js';

const LOCALE_KEY = 'vngrc_locale';

/** All supported locale codes for browser language matching. */
const ALL_LOCALE_CODES = LOCALE_OPTIONS.map((o) => o.value);

/**
 * A stored preference as a supported locale code, or `null` when it names none.
 * Exact codes pass; a language-only code (`vi`, from before full codes were
 * stored) resolves by prefix. Unlike `detectLocale` there is no English
 * fallback: a value that names no supported locale is not a choice to adopt.
 */
function supportedLocale(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  if (ALL_LOCALE_CODES.includes(value as any)) return value;
  const prefix = value.split('-')[0].toLowerCase();
  return ALL_LOCALE_CODES.find((code) => code.toLowerCase().startsWith(prefix + '-')) ?? null;
}

/** Match browser language to a supported locale (e.g. 'sv' → 'sv-SE', 'de-AT' → 'de-DE'). */
function detectLocale(browserLang: string): string {
  // Exact match (e.g. 'sv-SE')
  if (ALL_LOCALE_CODES.includes(browserLang as any)) return browserLang;
  // Prefix match (e.g. 'sv' → 'sv-SE', 'de' → 'de-DE')
  const prefix = browserLang.split('-')[0].toLowerCase();
  const match = ALL_LOCALE_CODES.find((code) => code.toLowerCase().startsWith(prefix + '-'));
  return match ?? Locale.EN;
}

interface LocaleContextValue {
  locale: string;
  setLocale: (locale: string) => void;
  t: TranslationKeys;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/** Whether this browser holds a cached locale (read once, on mount). */
function hasCachedLocale(): boolean {
  try {
    return typeof localStorage !== 'undefined' && !!localStorage.getItem(LOCALE_KEY);
  } catch {
    return false;
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  // Read before the initialiser below can be affected by any later write.
  const cachedAtBoot = useRef<boolean | null>(null);
  if (cachedAtBoot.current === null) cachedAtBoot.current = hasCachedLocale();
  const [locale, setLocaleState] = useState<string>(() => {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(LOCALE_KEY) : null;
    if (stored) return stored;
    const browserLang = typeof navigator !== 'undefined' ? navigator.language : 'vi';
    return detectLocale(browserLang);
  });

  // Sprint 64.70.0 — memoised on `locale`.
  //
  // `getTranslations` is not a lookup: outside `en-US` it deep-merged the whole
  // bundle on every call, measured at ~3 ms, and this line runs on every render
  // of the provider. `getTranslations` now caches per locale as well, so this
  // `useMemo` is the second of two layers rather than the only one — either
  // alone leaves `t` unstable in a way a consumer cannot see.
  const t: TranslationKeys = useMemo(() => getTranslations(locale), [locale]);

  // Sync <html lang> attribute with current locale (WCAG 3.1.1)
  const langCode = locale.split('-')[0];
  if (typeof document !== 'undefined') {
    document.documentElement.lang = langCode;
  }

  // Fix sprint #447 (round 2) — read the person's choice on boot; write nothing.
  //
  // The rule: the server preference holds the language the person last CHOSE,
  // on any device — `setLocale` (a person's change) is its only writer. On boot:
  //
  //   - with no cached locale, an unstamped non-default language (a choice
  //     recorded before the stamp existed) is adopted too — gate E12.5 P2-2;
  //   - a recorded choice (`language_chosen_at` set, written only when a
  //     person changes the language) that names a supported locale is adopted
  //     here, so a choice made on another device wins over this browser's
  //     cached value;
  //   - anything else is not a choice — nothing stored (the server answers with
  //     its defaults), or a record some other change created with the default
  //     language — and the browser's cached or detected locale stands;
  //   - a failed read changes nothing.
  //
  // This pushed localStorage to the server whenever the two differed. Since the
  // server answers `language: 'en'` when nothing is stored, every first page
  // load created a preferences record, and a device holding a stale cached
  // locale overwrote the choice the person had made on another device.
  useEffect(() => {
    // The whole body is guarded, not just the promise: an absent
    // `getPreferences` must not unmount every tree this provider wraps. A
    // synchronous failure does what a rejected read does — the locale stands.
    try {
      apiClient
        .getPreferences()
        .then((res: any) => {
          const stored = res?.data;
          if (!stored) return;
          const chosen = supportedLocale(stored.language);
          if (!chosen) return;
          // Gate E12.5 (P2-2) — a language recorded before `language_chosen_at`
          // existed carries no stamp. On a browser with no cached locale it is
          // adopted as the first visit always adopted it, unless it is the
          // default English the server answers with. A cached locale is kept
          // over an unstamped value; a stamped choice wins over both.
          const stamped = typeof stored.language_chosen_at === 'string';
          if (!stamped && (cachedAtBoot.current || chosen === Locale.EN)) return;
          setLocaleState(chosen);
          try {
            localStorage.setItem(LOCALE_KEY, chosen);
          } catch {
            // localStorage may be unavailable — the in-memory locale still applies.
          }
        })
        .catch((err) => console.warn('getPreferences failed', err));
    } catch {
      // Preserve the existing optional fallback; report this failed operation without payloads.
      logWarning('contexts/LocaleContext::LocaleProvider::3');
    }
  }, []); // Only on mount

  const setLocale = useCallback((newLocale: string) => {
    setLocaleState(newLocale);
    localStorage.setItem(LOCALE_KEY, newLocale);
    // Sync to server preferences
    apiClient
      .updatePreferences({ language: newLocale })
      .catch((err) => console.warn('updatePreferences failed', err));
  }, []);

  // Sprint 64.70.0 — the context value is memoised too.
  //
  // A fresh object literal here re-renders every consumer on every provider
  // render even when `locale`, `setLocale` and `t` are all unchanged. This is
  // the same shape Sprint 64.5.0 spent a sprint on in `ToastProvider`, where an
  // unmemoised value produced 241 calls in 800 ms and 24 `toastRef` workarounds
  // across the codebase. `LocaleProvider` was not touched then.
  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocaleContext(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocaleContext must be used within LocaleProvider');
  return ctx;
}
